import {
	createContext,
	PropsWithChildren,
	useCallback,
	useContext,
	useRef,
	useState,
} from 'react'

import { StructureTreeItem } from '@/components/TreeV2'
import { useAppDispatch } from '@/hooks'
import { loadSystemNodes } from '@/store/modules/node/actions'

type SystemContextMenuPos = { x: number; y: number }

interface TreeOperations {
	refreshParent: (parentId: string) => Promise<void>
	refreshParentSilent: (parentId: string) => Promise<void>
	refreshSystems: () => Promise<void>
	refreshSystemsSilent: () => Promise<void>
	revealItem: (itemId: string) => Promise<void>
	updateNodes: (
		nodeIds: number[],
		updates: Partial<Pick<StructureTreeItem, 'lockedFor' | 'lockedTime' | 'status'>>,
	) => void
	openSelectedSystemContextMenu: (x: number, y: number) => void
}

interface TreePanelContextValue {
	clearPendingRevealId: () => void
	clearPendingSystemContextMenu: () => void
	isCollapsed: boolean
	openSelectedSystemContextMenu: (x: number, y: number) => void
	pendingRevealId: string | null
	pendingSystemContextMenu: SystemContextMenuPos | null
	refreshParent: (parentId: string) => Promise<void>
	refreshParentSilent: (parentId: string) => Promise<void>
	refreshSystems: () => Promise<void>
	refreshSystemsSilent: () => Promise<void>
	registerTreeOperations: (operations: TreeOperations) => void
	requestRevealInTree: (itemId: string) => void
	requestSystemContextMenu: (x: number, y: number) => void
	resetTreeOperations: () => void
	revealItem: (itemId: string) => Promise<void>
	selectedSystemId: string | null
	setCollapsed: (collapsed: boolean) => void
	setSelectedSystemId: (systemId: string | null) => void
	toggleCollapsed: () => void
	updateNodes: (
		nodeIds: number[],
		updates: Partial<Pick<StructureTreeItem, 'lockedFor' | 'lockedTime' | 'status'>>,
	) => void
}

const TreePanelContext = createContext<TreePanelContextValue | null>(null)

const STORAGE_KEY = 'TREE_PANEL_COLLAPSED'
const SELECTED_SYSTEM_STORAGE_KEY = 'TREE_PANEL_SELECTED_SYSTEM_ID'

const noopAsync = async () => {}
const noopSync = () => {}

const DEFAULT_TREE_OPERATIONS: TreeOperations = {
	refreshSystems: noopAsync,
	refreshSystemsSilent: noopAsync,
	refreshParent: noopAsync,
	refreshParentSilent: noopAsync,
	revealItem: noopAsync,
	updateNodes: noopSync,
	openSelectedSystemContextMenu: noopSync,
}

const readStoredSystemId = (): string | null => {
	try {
		return localStorage.getItem(SELECTED_SYSTEM_STORAGE_KEY)
	} catch {
		return null
	}
}

export const TreePanelProvider = ({ children }: PropsWithChildren) => {
	const dispatch = useAppDispatch()
	const [isCollapsed, setIsCollapsed] = useState(() => {
		try {
			const stored = localStorage.getItem(STORAGE_KEY)
			return stored ? JSON.parse(stored) : false
		} catch {
			return false
		}
	})
	const [pendingRevealId, setPendingRevealId] = useState<string | null>(null)
	const [pendingSystemContextMenu, setPendingSystemContextMenu] =
		useState<SystemContextMenuPos | null>(null)
	const [selectedSystemId, setSelectedSystemIdState] = useState<string | null>(
		readStoredSystemId,
	)

	const treeOperationsRef = useRef<TreeOperations>(DEFAULT_TREE_OPERATIONS)

	const setCollapsed = useCallback((collapsed: boolean) => {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(collapsed))
		setIsCollapsed(collapsed)
	}, [])

	const setSelectedSystemId = useCallback((systemId: string | null) => {
		setSelectedSystemIdState(systemId)
		try {
			if (systemId) {
				localStorage.setItem(SELECTED_SYSTEM_STORAGE_KEY, systemId)
			} else {
				localStorage.removeItem(SELECTED_SYSTEM_STORAGE_KEY)
			}
		} catch {
			// ignore storage errors
		}
	}, [])

	const toggleCollapsed = useCallback(() => {
		setCollapsed(!isCollapsed)
	}, [isCollapsed, setCollapsed])

	const registerTreeOperations = useCallback((operations: TreeOperations) => {
		treeOperationsRef.current = operations
	}, [])

	const resetTreeOperations = useCallback(() => {
		treeOperationsRef.current = DEFAULT_TREE_OPERATIONS
	}, [])

	const requestRevealInTree = useCallback(
		(itemId: string) => {
			setCollapsed(false)
			setPendingRevealId(itemId)
		},
		[setCollapsed],
	)

	const clearPendingRevealId = useCallback(() => {
		setPendingRevealId(null)
	}, [])

	const clearPendingSystemContextMenu = useCallback(() => {
		setPendingSystemContextMenu(null)
	}, [])

	const refreshSystems = useCallback(async () => {
		await Promise.all([
			dispatch(loadSystemNodes()),
			treeOperationsRef.current.refreshSystems(),
		])
	}, [dispatch])

	const refreshSystemsSilent = useCallback(async () => {
		await Promise.all([
			dispatch(loadSystemNodes()),
			treeOperationsRef.current.refreshSystemsSilent(),
		])
	}, [dispatch])

	const refreshParent = useCallback(async (parentId: string) => {
		await treeOperationsRef.current.refreshParent(parentId)
	}, [])

	const refreshParentSilent = useCallback(async (parentId: string) => {
		await treeOperationsRef.current.refreshParentSilent(parentId)
	}, [])

	const revealItem = useCallback(async (itemId: string) => {
		await treeOperationsRef.current.revealItem(itemId)
	}, [])

	const updateNodes = useCallback(
		(
			nodeIds: number[],
			updates: Partial<
				Pick<StructureTreeItem, 'lockedFor' | 'lockedTime' | 'status'>
			>,
		) => {
			treeOperationsRef.current.updateNodes(nodeIds, updates)
		},
		[],
	)

	const openSelectedSystemContextMenu = useCallback((x: number, y: number) => {
		treeOperationsRef.current.openSelectedSystemContextMenu(x, y)
	}, [])

	const requestSystemContextMenu = useCallback((x: number, y: number) => {
		if (treeOperationsRef.current !== DEFAULT_TREE_OPERATIONS) {
			treeOperationsRef.current.openSelectedSystemContextMenu(x, y)
			return
		}
		setPendingSystemContextMenu({ x, y })
	}, [])

	return (
		<TreePanelContext.Provider
			value={{
				isCollapsed,
				setCollapsed,
				toggleCollapsed,
				pendingRevealId,
				requestRevealInTree,
				clearPendingRevealId,
				pendingSystemContextMenu,
				requestSystemContextMenu,
				clearPendingSystemContextMenu,
				selectedSystemId,
				setSelectedSystemId,
				registerTreeOperations,
				resetTreeOperations,
				refreshSystems,
				refreshSystemsSilent,
				refreshParent,
				refreshParentSilent,
				revealItem,
				updateNodes,
				openSelectedSystemContextMenu,
			}}
		>
			{children}
		</TreePanelContext.Provider>
	)
}

export const useTreePanelCollapsed = () => {
	const context = useContext(TreePanelContext)
	if (!context) {
		return {
			isCollapsed: false,
			setCollapsed: () => {},
			toggleCollapsed: () => {},
		}
	}
	return {
		isCollapsed: context.isCollapsed,
		setCollapsed: context.setCollapsed,
		toggleCollapsed: context.toggleCollapsed,
	}
}

export const useTreePanelOperations = () => {
	const context = useContext(TreePanelContext)
	if (!context) {
		return {
			refreshSystems: noopAsync,
			refreshSystemsSilent: noopAsync,
			refreshParent: noopAsync,
			refreshParentSilent: noopAsync,
			revealItem: noopAsync,
			updateNodes: noopSync,
			registerTreeOperations: () => {},
			resetTreeOperations: () => {},
			pendingRevealId: null as string | null,
			requestRevealInTree: () => {},
			clearPendingRevealId: () => {},
			pendingSystemContextMenu: null as SystemContextMenuPos | null,
			requestSystemContextMenu: noopSync,
			clearPendingSystemContextMenu: () => {},
			selectedSystemId: null as string | null,
			setSelectedSystemId: () => {},
			openSelectedSystemContextMenu: noopSync,
		}
	}
	return {
		refreshSystems: context.refreshSystems,
		refreshSystemsSilent: context.refreshSystemsSilent,
		refreshParent: context.refreshParent,
		refreshParentSilent: context.refreshParentSilent,
		revealItem: context.revealItem,
		updateNodes: context.updateNodes,
		registerTreeOperations: context.registerTreeOperations,
		resetTreeOperations: context.resetTreeOperations,
		pendingRevealId: context.pendingRevealId,
		requestRevealInTree: context.requestRevealInTree,
		clearPendingRevealId: context.clearPendingRevealId,
		pendingSystemContextMenu: context.pendingSystemContextMenu,
		requestSystemContextMenu: context.requestSystemContextMenu,
		clearPendingSystemContextMenu: context.clearPendingSystemContextMenu,
		selectedSystemId: context.selectedSystemId,
		setSelectedSystemId: context.setSelectedSystemId,
		openSelectedSystemContextMenu: context.openSelectedSystemContextMenu,
	}
}

export const useSelectedSystem = () => {
	const context = useContext(TreePanelContext)
	if (!context) {
		return {
			selectedSystemId: null as string | null,
			setSelectedSystemId: (_systemId: string | null) => {},
			openSelectedSystemContextMenu: noopSync,
			requestSystemContextMenu: noopSync,
		}
	}
	return {
		selectedSystemId: context.selectedSystemId,
		setSelectedSystemId: context.setSelectedSystemId,
		openSelectedSystemContextMenu: context.openSelectedSystemContextMenu,
		requestSystemContextMenu: context.requestSystemContextMenu,
	}
}
